import React, { useEffect, useState } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { adminService, User, Hospital } from '@/services/adminService';
import { formatUserLevel } from '@/utils/formatters';
import { 
  Users, 
  UserPlus, 
  Edit, 
  Trash2, 
  Search,
  AlertTriangle,
  Check,
  X,
  UserCheck,
  UserX,
  Heart,
  Baby
} from 'lucide-react';

export const UsersManagement: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [hospitals, setHospitals] = useState<Hospital[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterHospital, setFilterHospital] = useState<string>('all');
  const [filterLevel, setFilterLevel] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Form data
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    user_level: 'maternity',
    hospital_id: null as number | null,
    assigned_section: 'chd' as 'chd' | 'prematuri',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [usersData, hospitalsData] = await Promise.all([
        adminService.listUsers(),
        adminService.listHospitals()
      ]);
      setUsers(usersData);
      setHospitals(hospitalsData);
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Eroare la încărcarea datelor');
      setLoading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      setError(null);
      await adminService.createUser(formData);
      setSuccessMessage('Utilizator adăugat cu succes!');
      setShowAddDialog(false);
      resetForm();
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la adăugarea utilizatorului');
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      const { password, ...rest } = formData;
      const updates = password ? formData : rest;
      await adminService.updateUser(selectedUser.user_id, updates);
      setSuccessMessage('Utilizator actualizat cu succes!');
      setShowEditDialog(false);
      resetForm();
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la actualizarea utilizatorului');
    }
  };

  const handleDeactivateUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await adminService.deactivateUser(selectedUser.user_id);
      setSuccessMessage('Utilizator dezactivat cu succes!');
      setShowDeactivateDialog(false);
      setSelectedUser(null);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la dezactivarea utilizatorului');
    }
  };

  const handleActivateUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await adminService.activateUser(selectedUser.user_id);
      setSuccessMessage('Utilizator activat cu succes!');
      setShowActivateDialog(false);
      setSelectedUser(null);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la activarea utilizatorului');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      setError(null);
      await adminService.deleteUser(selectedUser.user_id);
      setSuccessMessage('Utilizator șters permanent cu succes!');
      setShowDeleteDialog(false);
      setSelectedUser(null);
      await loadData();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Eroare la ștergerea utilizatorului');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      user_level: 'maternity',
      hospital_id: null,
      assigned_section: 'chd'
    });
    setSelectedUser(null);
  };

  const openEditDialog = (user: User) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name,
      email: user.email,
      user_level: user.user_level,
      hospital_id: user.hospital_id,
      assigned_section: user.assigned_section || 'chd'
    });
    setShowEditDialog(true);
  };

  const openDeactivateDialog = (user: User) => {
    setSelectedUser(user);
    setShowDeactivateDialog(true);
  };

  const openActivateDialog = (user: User) => {
    setSelectedUser(user);
    setShowActivateDialog(true);
  };

  const openDeleteDialog = (user: User) => {
    setSelectedUser(user);
    setShowDeleteDialog(true);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesHospital = filterHospital === 'all' || user.hospital_id?.toString() === filterHospital;
    const matchesLevel = filterLevel === 'all' || user.user_level === filterLevel;
    const matchesActive = filterActive === 'all' || 
                         (filterActive === 'active' && user.is_active) ||
                         (filterActive === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesHospital && matchesLevel && matchesActive;
  });

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Gestionare utilizatori</h1>
            <p className="text-muted-foreground">
              Administrează utilizatorii sistemului
            </p>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
                <UserPlus className="mr-2 h-4 w-4" />
                Adaugă utilizator
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>Adaugă utilizator nou</DialogTitle>
                <DialogDescription>
                  Completează informațiile pentru noul utilizator
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="username">Nume utilizator</Label>
                  <Input
                    id="username"
                    value={formData.username}
                    onChange={(e) => setFormData({...formData, username: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="password">Parolă</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="full_name">Nume complet</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="user_level">Nivel acces</Label>
                  <Select 
                    value={formData.user_level}
                    onValueChange={(value) => setFormData({...formData, user_level: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="maternity">Maternitate</SelectItem>
                      <SelectItem value="hospital">Spital</SelectItem>
                      <SelectItem value="administrator">Administrator</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {formData.user_level !== 'administrator' && (
                  <div className="grid gap-2">
                    <Label htmlFor="hospital">Spital</Label>
                    <Select 
                      value={formData.hospital_id?.toString() || ''}
                      onValueChange={(value) => setFormData({...formData, hospital_id: value ? parseInt(value) : null})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectează spital" />
                      </SelectTrigger>
                      <SelectContent>
                        {hospitals.map(hospital => (
                          <SelectItem key={hospital.id || hospital.hospital_id} value={(hospital.id || hospital.hospital_id || 0).toString()}>
                            {hospital.name || hospital.hospital_name || ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {formData.user_level === 'hospital' && (
                  <div className="grid gap-2">
                    <Label htmlFor="assigned_section">Registru</Label>
                    <Select
                      value={formData.assigned_section}
                      onValueChange={(value) => setFormData({...formData, assigned_section: value as 'chd' | 'prematuri'})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="chd">
                          <span className="flex items-center gap-2">
                            <Heart className="h-4 w-4 text-red-600" />
                            Registru MCC
                          </span>
                        </SelectItem>
                        <SelectItem value="prematuri">
                          <span className="flex items-center gap-2">
                            <Baby className="h-4 w-4 text-purple-600" />
                            Copii prematuri
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {formData.user_level === 'administrator' && (
                  <div className="grid gap-2">
                    <Label htmlFor="hospital">Spital (opțional)</Label>
                    <Select 
                      value={formData.hospital_id?.toString() || 'none'}
                      onValueChange={(value) => setFormData({...formData, hospital_id: value === 'none' ? null : parseInt(value)})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selectează spital (opțional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Fără spital</SelectItem>
                        {hospitals.map(hospital => (
                          <SelectItem key={hospital.id || hospital.hospital_id} value={(hospital.id || hospital.hospital_id || 0).toString()}>
                            {hospital.name || hospital.hospital_name || ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                  Anulează
                </Button>
                <Button onClick={handleAddUser}>Adaugă</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Alerts */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {successMessage && (
          <Alert className="border-green-500 bg-green-50">
            <Check className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">{successMessage}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filtre</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-4">
              <div className="flex items-center gap-2">
                <Search className="h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Caută utilizatori..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <Select value={filterHospital} onValueChange={setFilterHospital}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate spitalele" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate spitalele</SelectItem>
                  {hospitals.map(hospital => (
                    <SelectItem key={hospital.id || hospital.hospital_id} value={(hospital.id || hospital.hospital_id || 0).toString()}>
                      {hospital.name || hospital.hospital_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterLevel} onValueChange={setFilterLevel}>
                <SelectTrigger>
                  <SelectValue placeholder="Toate nivelurile" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate nivelurile</SelectItem>
                  <SelectItem value="maternity">Maternitate</SelectItem>
                  <SelectItem value="hospital">Spital</SelectItem>
                  <SelectItem value="administrator">Administrator</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterActive} onValueChange={setFilterActive}>
                <SelectTrigger>
                  <SelectValue placeholder="Toți utilizatorii" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toți utilizatorii</SelectItem>
                  <SelectItem value="active">Activi</SelectItem>
                  <SelectItem value="inactive">Inactivi</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              Utilizatori ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Utilizator</TableHead>
                  <TableHead>Nivel</TableHead>
                  <TableHead>Spital</TableHead>
                  <TableHead>Registru</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{user.full_name}</div>
                        <div className="text-xs text-muted-foreground">{user.username} · {user.email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {formatUserLevel(user.user_level_id)}
                      </Badge>
                    </TableCell>
                    <TableCell>{user.hospital_name || '-'}</TableCell>
                    <TableCell>
                      {user.user_level === 'hospital' ? (
                        <Badge variant="outline" className={user.assigned_section === 'prematuri' ? 'border-purple-500 text-purple-600' : 'border-red-500 text-red-600'}>
                          {user.assigned_section === 'prematuri' ? 'Prematuri' : 'MCC'}
                        </Badge>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'destructive'}>
                        {user.is_active ? 'Activ' : 'Inactiv'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(user)}
                          title="Editează utilizator"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user.is_active ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeactivateDialog(user)}
                              title="Dezactivează utilizator"
                            >
                              <UserX className="h-4 w-4 text-orange-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(user)}
                              title="Șterge permanent utilizator"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openActivateDialog(user)}
                              title="Activează utilizator"
                            >
                              <UserCheck className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDeleteDialog(user)}
                              title="Șterge permanent utilizator"
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Editează utilizator</DialogTitle>
              <DialogDescription>
                Modifică informațiile utilizatorului
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-username">Nume utilizator</Label>
                <Input
                  id="edit-username"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-password">Parolă nouă (opțional)</Label>
                <Input
                  id="edit-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="Lasă gol pentru a păstra parola actuală"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-full_name">Nume complet</Label>
                <Input
                  id="edit-full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-user_level">Nivel acces</Label>
                <Select 
                  value={formData.user_level}
                  onValueChange={(value) => setFormData({...formData, user_level: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="maternity">Maternitate</SelectItem>
                    <SelectItem value="hospital">Spital</SelectItem>
                    <SelectItem value="administrator">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {formData.user_level !== 'administrator' && (
                <div className="grid gap-2">
                  <Label htmlFor="edit-hospital">Spital</Label>
                  <Select 
                    value={formData.hospital_id?.toString() || ''}
                    onValueChange={(value) => setFormData({...formData, hospital_id: value ? parseInt(value) : null})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează spital" />
                    </SelectTrigger>
                    <SelectContent>
                      {hospitals.map(hospital => (
                        <SelectItem key={hospital.id || hospital.hospital_id} value={(hospital.id || hospital.hospital_id || 0).toString()}>
                          {hospital.name || hospital.hospital_name || ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.user_level === 'hospital' && (
                <div className="grid gap-2">
                  <Label htmlFor="edit-assigned_section">Registru</Label>
                  <Select
                    value={formData.assigned_section}
                    onValueChange={(value) => setFormData({...formData, assigned_section: value as 'chd' | 'prematuri'})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="chd">
                        <span className="flex items-center gap-2">
                          <Heart className="h-4 w-4 text-red-600" />
                          Registru MCC
                        </span>
                      </SelectItem>
                      <SelectItem value="prematuri">
                        <span className="flex items-center gap-2">
                          <Baby className="h-4 w-4 text-purple-600" />
                          Copii prematuri
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.user_level === 'administrator' && (
                <div className="grid gap-2">
                  <Label htmlFor="edit-hospital">Spital (opțional)</Label>
                  <Select 
                    value={formData.hospital_id?.toString() || 'none'}
                    onValueChange={(value) => setFormData({...formData, hospital_id: value === 'none' ? null : parseInt(value)})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selectează spital (opțional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Fără spital</SelectItem>
                      {hospitals.map(hospital => (
                        <SelectItem key={hospital.id || hospital.hospital_id} value={(hospital.id || hospital.hospital_id || 0).toString()}>
                          {hospital.name || hospital.hospital_name || ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Anulează
              </Button>
              <Button onClick={handleEditUser}>Salvează</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Deactivate Confirmation Dialog */}
        <Dialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmare dezactivare</DialogTitle>
              <DialogDescription>
                Ești sigur că vrei să dezactivezi utilizatorul <strong>{selectedUser?.full_name}</strong>?
                Această acțiune poate fi reversibilă prin reactivare.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeactivateDialog(false)}>
                Anulează
              </Button>
              <Button variant="secondary" onClick={handleDeactivateUser}>
                <UserX className="mr-2 h-4 w-4" />
                Dezactivează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Activate Confirmation Dialog */}
        <Dialog open={showActivateDialog} onOpenChange={setShowActivateDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmare activare</DialogTitle>
              <DialogDescription>
                Ești sigur că vrei să activezi utilizatorul <strong>{selectedUser?.full_name}</strong>?
                Utilizatorul va putea accesa din nou sistemul.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowActivateDialog(false)}>
                Anulează
              </Button>
              <Button onClick={handleActivateUser} className="bg-green-600 hover:bg-green-700 text-white">
                <UserCheck className="mr-2 h-4 w-4" />
                Activează
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmare ștergere permanentă</DialogTitle>
              <DialogDescription>
                <div className="space-y-2">
                  <p>Ești sigur că vrei să ștergi permanent utilizatorul <strong>{selectedUser?.full_name}</strong>?</p>
                  <p className="text-red-600 font-medium">
                    ⚠️ Această acțiune este IREVERSIBILĂ și va șterge definitiv toate datele asociate utilizatorului.
                  </p>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Anulează
              </Button>
              <Button variant="destructive" onClick={handleDeleteUser}>
                <Trash2 className="mr-2 h-4 w-4" />
                Șterge permanent
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};